'use client';

import {
  buildUserBlockingFailureProperties,
  USER_BLOCKING_FAILURE_EVENT,
  type UserBlockingFailure,
} from '../../../lib/observability/user-blocking-failure';
import { browserWorkspaceForPath } from '../../../lib/observability/workspace-error-context';
import { buildPageCrashProperties } from '../../../lib/observability/page-crash-detail';
import {
  buildCrashDiagnosis,
  isPublicMarketingRoute,
} from '../../../lib/observability/page-crash-diagnosis';
import { userBlockingRoutePattern } from '../../../lib/observability/user-blocking-page-path';
import { ConvexError } from 'convex/values';
import { isAccessDeniedError, isAuthLapseError } from '@/lib/error-utils';
import {
  sendPageCrashEvent,
  sendUserBlockingFailureFromLoadedCode,
  type LoadedPostHogClient,
} from './user-blocking-failure-beacon';

/** One send per failure group per tab per minute; Discord dedupes the rest. */
const REPORT_THROTTLE_MS = 60_000;
const lastReportAt = new Map<string, number>();

/** Whether `alertKey` may send now; remembers it when it may. */
function claimReportSlot(alertKey: string, now: number): boolean {
  const last = lastReportAt.get(alertKey);
  if (last !== undefined && now - last < REPORT_THROTTLE_MS) return false;
  // Forget the oldest groups, never all of them: a clear would let every
  // group repeat at once.
  while (lastReportAt.size >= 200) {
    const oldest = lastReportAt.keys().next().value;
    if (oldest === undefined) break;
    lastReportAt.delete(oldest);
  }
  lastReportAt.set(alertKey, now);
  return true;
}

/**
 * Page crashes sent per tab in a window, across all failure groups: the
 * per-group throttle above cannot stop a burst of distinct crashes.
 */
const PAGE_CRASH_BUDGET = 10;
const PAGE_CRASH_BUDGET_WINDOW_MS = 10 * 60_000;
const pageCrashSentAt: number[] = [];

/** Whether a page crash may still be sent; checked before any work on it. */
function pageCrashBudgetLeft(now: number): boolean {
  while (
    pageCrashSentAt.length > 0 &&
    now - (pageCrashSentAt[0] ?? 0) > PAGE_CRASH_BUDGET_WINDOW_MS
  ) {
    pageCrashSentAt.shift();
  }
  return pageCrashSentAt.length < PAGE_CRASH_BUDGET;
}

const BOUNDED_MESSAGE_CHARS = 2000;
const BOUNDED_STACK_CHARS = 6000;

/**
 * A copy of `error` with every field a classifier may read cut to a fixed
 * size, so no check on a crash costs more than that, whatever the error
 * carries. Only the class, digest, a capped message and a `ConvexError`'s
 * short string survive; an object `data` is never serialized.
 */
function boundedCrashError(error: unknown): Error {
  const read = (key: string): string => {
    const value =
      typeof error === 'object' && error !== null
        ? (error as Record<string, unknown>)[key]
        : undefined;
    return typeof value === 'string' ? value : '';
  };
  let bounded: Error;
  if (error instanceof ConvexError) {
    const data = (error as { data?: unknown }).data;
    bounded = new ConvexError(
      typeof data === 'string' ? data.slice(0, 200) : ''
    );
  } else {
    bounded = new Error();
  }
  bounded.message =
    typeof error === 'string'
      ? error.slice(0, BOUNDED_MESSAGE_CHARS)
      : read('message').slice(0, BOUNDED_MESSAGE_CHARS);
  bounded.name = read('name').slice(0, 80) || bounded.name;
  const digest = read('digest').slice(0, 40);
  if (digest) (bounded as { digest?: string }).digest = digest;
  // Capped, and read only to find the failing first-party frame (#7302); the
  // beacon is built field by field, so no stack text is ever sent.
  bounded.stack = read('stack').slice(0, BOUNDED_STACK_CHARS);
  return bounded;
}

/**
 * One page crash, sent by itself (not through posthog-js, whose enrichment
 * with person/session/persisted properties this app cannot control): the
 * error class, digest, route pattern and, for a script load failure, the
 * first-party bundle file. Same 60 s per-group throttle and crash budget.
 */
function sendPageCrash(bounded: Error, operation?: string): void {
  const route = userBlockingRoutePattern(
    window.location?.pathname,
    browserWorkspaceForPath(window.location?.pathname)?.slug
  );
  const properties = buildPageCrashProperties(bounded, {
    operation,
    routePattern: route,
    firstPartyHost: window.location?.host,
  });
  const now = Date.now();
  if (!claimReportSlot(properties.alert_key, now)) return;
  pageCrashSentAt.push(now);
  // A SyntaxError / chunk-load crash on a PUBLIC marketing page also carries
  // a reason ENUM (a fixed vocabulary, never any part of the message), plus the
  // failing script and line:col when the browser gives a frame (#7302); no
  // other route or class does.
  const diagnose = isPublicMarketingRoute(window.location?.pathname);
  sendPageCrashEvent(
    {
      ...properties,
      ...(diagnose ? buildCrashDiagnosis(bounded, window.location?.host) : {}),
    },
    { allowDiagnosis: diagnose }
  );
}

type PostHogBrowserClient =
  typeof import('../../../lib/observability/posthog-browser-client');
let browserClient: Promise<PostHogBrowserClient> | null = null;

/** Loaded on the first report, so importing this module costs nothing. */
function loadBrowserClient(): Promise<PostHogBrowserClient> {
  browserClient ??= import('../../../lib/observability/posthog-browser-client');
  return browserClient;
}

/** Test seam: forget the throttle between cases. */
export function resetUserBlockingFailureThrottle(): void {
  lastReportAt.clear();
  pageCrashSentAt.length = 0;
}

/**
 * Reports a failure that stopped the user's work (#6012). Fire-and-forget:
 * it runs after the current task, loads the PostHog client lazily (so every
 * module that reports stays import-safe outside the browser), sends without
 * the batch queue so the #alerting destination sees it within seconds, and
 * never throws into or waits on the caller's UI path.
 */
export function reportUserBlockingFailure(
  failure: UserBlockingFailure & {
    /** The page the work started on, when the user may have left it since. */
    pathname?: string | null;
    /**
     * The caller showed the user a clear reason they can act on (an archived
     * task, a full drive), so this is a product state, not an alert. Only a
     * caller that shows such a reason may set it; it is never a default.
     */
    explainedToUser?: boolean;
    /**
     * An already-loaded posthog-js the caller imports. With it the report
     * sends synchronously, with no lazy import, falling back to a beacon:
     * for failures where no further chunk can load (#6155).
     */
    loadedClient?: LoadedPostHogClient | null;
  }
): void {
  if (typeof window === 'undefined' || failure.explainedToUser) return;
  try {
    if (failure.surface === 'page_crash') {
      // A page crash is always the minimal independent event, whoever asks
      // (the boundaries, or the chunk-load fallback with its own client).
      if (!pageCrashBudgetLeft(Date.now())) return;
      sendPageCrash(boundedCrashError(failure.error), failure.operation);
      return;
    }
    const {
      pathname = window.location?.pathname,
      explainedToUser: _explainedToUser,
      loadedClient,
      ...rest
    } = failure;
    const properties = buildUserBlockingFailureProperties(rest, {
      pathname,
      origin: window.location?.origin,
      workspaceSlug: browserWorkspaceForPath(pathname)?.slug,
    });
    if (!claimReportSlot(properties.alert_key, Date.now())) return;
    if (loadedClient !== undefined) {
      sendUserBlockingFailureFromLoadedCode(properties, loadedClient);
      return;
    }
    window.setTimeout(() => {
      void loadBrowserClient()
        .then(({ ensurePostHogBrowserInitialized, posthog }) => {
          if (!ensurePostHogBrowserInitialized()) return;
          posthog.capture(USER_BLOCKING_FAILURE_EVENT, properties, {
            send_instantly: true,
          });
        })
        .catch(() => {
          // Alerting must never break the page it reports on.
        });
    }, 0);
  } catch {
    // Same: a reporting failure is never the user's problem.
  }
}

/** Runs `request`; a failure is reported (see above) and rethrown unchanged. */
export async function withUserBlockingFailureReport<T>(
  failure: Omit<UserBlockingFailure, 'error'>,
  request: () => Promise<T>
): Promise<T> {
  try {
    return await request();
  } catch (error) {
    reportUserBlockingFailure({ ...failure, error });
    throw error;
  }
}

/**
 * A crash an error boundary shows instead of the page. A tab out of date
 * with a deploy, a lapsed session or a page the user may not see recovers
 * on its own or is expected, so none of them alerts. An exhausted crash
 * budget is refused first, before the error is read at all.
 */
export function reportPageCrash(
  error: unknown,
  { deploySkew = false }: { deploySkew?: boolean } = {}
): void {
  try {
    if (typeof window === 'undefined') return;
    if (!pageCrashBudgetLeft(Date.now())) return;
    if (deploySkew) return;
    const bounded = boundedCrashError(error);
    if (isAuthLapseError(bounded) || isAccessDeniedError(bounded)) return;
    reportUserBlockingFailure({ surface: 'page_crash', error: bounded });
  } catch {
    // Alerting must never break the page it reports on.
  }
}
